import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message, Alert } from 'antd';
import { TeamOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiService from '@/services/api.service';
import { logger } from '@/utils/logger';
import { ResetPasswordContainer, StyledCard } from './styles/ResetPassword.styles';

const { Title, Text } = Typography;

interface InvitationData {
  token: string;
  email: string;
  clubId: number;
  clubName: string;
  role: string;
  expiresAt: string;
  inviterName: string | null;
}

const AcceptManagerInvitation: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response: any = await apiService.get(`/manager-invitations/accept?token=${token}`);
        if (response.data?.success && response.data?.data) {
          setInvitation(response.data.data);
        } else {
          setInvitation(null);
        }
      } catch (error: any) {
        logger.error('Error fetching invitation:', error);
        setInvitation(null);
      } finally {
        setVerifying(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleSubmit = async (values: { password?: string; confirmPassword?: string; firstName?: string; lastName?: string }) => {
    if (!token) return;

    if (values.password && values.password !== values.confirmPassword) {
      message.error('Пароли не совпадают');
      return;
    }

    try {
      setLoading(true);
      await apiService.post('/manager-invitations/accept', {
        token,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
      });
      setSuccess(true);
      message.success('Приглашение принято! Вы можете войти в систему.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error: any) {
      logger.error('Error accepting invitation:', error);
      message.error(error.response?.data?.message || 'Не удалось принять приглашение');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <ResetPasswordContainer>
        <StyledCard>
          <div style={{ textAlign: 'center' }}>
            <Title level={3}>Загрузка приглашения...</Title>
          </div>
        </StyledCard>
      </ResetPasswordContainer>
    );
  }

  if (!invitation) {
    return (
      <ResetPasswordContainer>
        <StyledCard>
          <Alert
            message="Приглашение не найдено"
            description="Ссылка недействительна или истекла. Обратитесь к администратору клуба за новым приглашением."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <div style={{ textAlign: 'center' }}>
            <Button type="primary" onClick={() => navigate('/login')}>
              Перейти к входу
            </Button>
          </div>
        </StyledCard>
      </ResetPasswordContainer>
    );
  }

  if (success) {
    return (
      <ResetPasswordContainer>
        <StyledCard>
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
            <Title level={3}>Вы стали менеджером клуба {invitation.clubName}</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              Вы будете перенаправлены на страницу входа...
            </Text>
            <Link to="/login">
              <Button type="primary">Перейти к входу</Button>
            </Link>
          </div>
        </StyledCard>
      </ResetPasswordContainer>
    );
  }

  const needPassword = true;

  return (
    <ResetPasswordContainer>
      <StyledCard>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <TeamOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={2}>Приглашение в клуб</Title>
          <Text type="secondary" style={{ display: 'block' }}>
            Вас приглашают стать менеджером клуба <strong>{invitation.clubName}</strong>
          </Text>
          {invitation.inviterName && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Приглашение от: {invitation.inviterName}
            </Text>
          )}
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            Email: {invitation.email}
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit} size="large">
          {needPassword && (
            <>
              <Form.Item
                name="firstName"
                label="Имя"
              >
                <Input placeholder="Имя" />
              </Form.Item>
              <Form.Item
                name="lastName"
                label="Фамилия"
              >
                <Input placeholder="Фамилия" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Пароль (если у вас ещё нет аккаунта)"
                rules={[
                  { min: 8, message: 'Минимум 8 символов' },
                ]}
              >
                <Input.Password placeholder="Пароль" autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Подтверждение пароля"
                dependencies={['password']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Пароли не совпадают'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Подтвердите пароль" autoComplete="new-password" />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Принять приглашение
            </Button>
          </Form.Item>
        </Form>
      </StyledCard>
    </ResetPasswordContainer>
  );
};

export default AcceptManagerInvitation;
